import { useNavigate } from "react-router-dom";
import { Logo } from "../components/Logo";
import { E32 } from "../theme/endesga32";
import { pixelTheme, type Theme } from "../theme/pixel";
import { defaultBtn } from "../theme/styles";

// Reached by clicking the yellow "InDev!" splash on the logo. A
// public page (logged in or out) that spells out the alpha disclaimer:
// the game is unfinished, progress is wiped repeatedly, players share a
// common card pool to keep generation costs down, and some cards in the
// first batch are broken because they were generated by a cheaper model.
export function InDevelopmentPage() {
  const theme = pixelTheme;
  const navigate = useNavigate();

  return (
    <div
      style={{
        // `position: fixed; inset: 0` fills the viewport without
        // viewport units (forbidden by the CSS allowlist) and still
        // lets the panel scroll on short screens.
        position: "fixed",
        inset: 0,
        overflow: "auto",
        background: theme.pageBg,
        color: theme.ink,
        fontFamily: theme.fontBody,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        style={{
          background: theme.panel,
          border: `2px solid ${theme.panelEdgeStrong}`,
          padding: 28,
          width: "100%",
          maxWidth: 560,
          marginTop: 40,
          marginBottom: 40,
          display: "grid",
          gap: 20,
          boxShadow: `6px 6px 0 0 ${theme.panelEdgeStrong}`,
        }}
      >
        <div style={{ display: "flex", justifyContent: "center" }}>
          {/* Suppress the splash here — it would just link back to
              this very page. */}
          <Logo s={1.4} indev={false} />
        </div>

        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 18,
            letterSpacing: theme.letterSpacing,
            color: theme.accent,
            textAlign: "center",
            lineHeight: 1.4,
          }}
        >
          THIS GAME IS IN DEVELOPMENT
        </div>

        <p style={{ margin: 0, fontSize: 16, lineHeight: 1.6, color: theme.ink }}>
          Deck of Infinity is in early <strong>alpha</strong>. It&apos;s
          unfinished and buggy. Thank you for trying it early.
        </p>

        <Point theme={theme} heading="Your progress will be reset">
          We will wipe all progress <strong>several times</strong> during
          development. Don&apos;t get too attached!
        </Point>

        <Point theme={theme} heading="Cards are shared for now">
          In the final game, every card you find will be unique. To save
          costs during development, you can currently find the{" "}
          <strong>same cards</strong> as other players, instead of each
          card being generated fresh just for you.
        </Point>

        <Point theme={theme} heading="Some cards may be broken">
          Most cards should work great, but some cards will be broken,
          since the first batch of cards has been generated using an
          inexpensive, less capable AI model for the purposes of keeping
          costs down. This will be far less of an issue in the final game.
        </Point>

        <div style={{ display: "flex", justifyContent: "center" }}>
          <button onClick={() => navigate(-1)} {...defaultBtn(theme)}>
            BACK
          </button>
        </div>
      </div>
    </div>
  );
}

function Point({
  theme,
  heading,
  children,
}: {
  theme: Theme;
  heading: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: "grid",
        gap: 6,
        padding: 14,
        background: theme.bgDeep,
        border: `1px solid ${theme.panelEdge}`,
        borderLeft: `4px solid ${E32.butter}`,
      }}
    >
      <div
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 11,
          letterSpacing: theme.letterSpacing,
          color: theme.ink,
        }}
      >
        {heading.toUpperCase()}
      </div>
      <div style={{ fontSize: 15, lineHeight: 1.6, color: theme.ink }}>
        {children}
      </div>
    </div>
  );
}
